// Copy for the public website. Keep it truthful: anything listed as "Live"
// must work end to end on a correctly configured server.

export const STEPS = [
    {
        n: "01",
        title: "Find",
        body: "Type your business name and city. We look up your Google listing, website, Instagram, Facebook and LinkedIn, plus your phone and address — and show you the evidence for each match.",
    },
    {
        n: "02",
        title: "Confirm",
        body: "You approve what's right, fix what's wrong, and add anything we missed. Nothing is saved to your profile until you say so.",
    },
    {
        n: "03",
        title: "Analyse",
        body: "Each profile is checked on its own, then an AI review pulls it together: an overall score, what's inconsistent between platforms, and the fixes that matter most.",
    },
    {
        n: "04",
        title: "Act",
        body: "Fixes, review replies, comment replies and WhatsApp answers are drafted in your voice and wait in one approvals inbox. Turn on autopilot only where you're comfortable.",
    },
];

export const FEATURES = [
    {
        key: "presence",
        icon: "Radar",
        title: "Online presence detection",
        body: "Finds your Google Business Profile, website and social pages from just a name and city, with a confidence score and the reason behind every match.",
    },
    {
        key: "analysis",
        icon: "Gauge",
        title: "Profile-by-profile analysis",
        body: "Checks hours, contact details, descriptions, links and activity on every profile, flags mismatches between them, and ranks the fixes.",
    },
    {
        key: "reviews",
        icon: "Star",
        title: "Review replies in your voice",
        body: "Drafts replies that follow your tone, your approved claims and your reply protocol. Anything touching legal threats, injuries, medical or privacy issues always comes to you first.",
    },
    {
        key: "comments",
        icon: "Instagram",
        title: "Instagram comment moderation",
        body: "Connect Instagram and new comments are sorted into questions, complaints, praise and spam. Spam can be hidden automatically; replies wait for approval.",
    },
    {
        key: "whatsapp",
        icon: "MessageCircle",
        title: "WhatsApp Business inbox",
        body: "Answers routine questions on the WhatsApp Cloud API with rules you set, and hands the conversation to your team the moment it needs a person.",
    },
    {
        key: "seo",
        icon: "Globe",
        title: "Website & SEO audit",
        body: "Crawls your site for technical, on-page and local SEO issues and pulls real clicks, searches and visitors from Search Console and Google Analytics.",
    },
    {
        key: "aeo",
        icon: "Sparkles",
        title: "AI search visibility",
        body: "Shows whether AI assistants are likely to recommend you, which questions you should answer on your site, and how you compare with competitors.",
    },
    {
        key: "manager",
        icon: "Bot",
        title: "AI Manager",
        body: "Ask “why did my rating drop?” or “reply to today's reviews” and get an answer grounded in your own data — with actions that go through your approvals.",
    },
    {
        key: "reports",
        icon: "BarChart3",
        title: "Briefings, alerts & reports",
        body: "A daily briefing, alerts when ratings or sentiment move, and weekly reports you can download and share.",
    },
];

// status: "live" = works with your own account once connected;
// "analysis" = we read the public page for analysis only;
// "soon" = on the roadmap, shown in the app as a demo.
export const INTEGRATIONS = [
    { name: "Google Business Profile", status: "analysis", note: "Found and checked via Google Places. Review sync and posting replies is coming." },
    { name: "Instagram", status: "live", note: "Instagram Login: read and moderate comments, publish approved replies." },
    { name: "WhatsApp Business", status: "live", note: "Meta Cloud API: receive messages and send approved or rule-based replies." },
    { name: "Google Search Console", status: "live", note: "Clicks, impressions, top searches and pages." },
    { name: "Google Analytics 4", status: "live", note: "Sessions, visitors, engagement and traffic sources." },
    { name: "Your website", status: "live", note: "Full crawl for SEO, AEO and consistency checks." },
    { name: "Facebook", status: "analysis", note: "Public page checked during presence analysis." },
    { name: "LinkedIn", status: "analysis", note: "Public company page checked during presence analysis." },
];

export const STATUS_LABEL = {
    live: "Live",
    analysis: "Analysis",
    soon: "Coming soon",
};

// Prices are set by you. Leave `price` empty to show "Talk to us".
export const PLANS = [
    {
        name: "Starter",
        price: "",
        period: "",
        blurb: "One business getting its online presence in order.",
        features: [
            "1 business, 1 location",
            "Presence detection & analysis",
            "Review reply drafting with approvals",
            "Website & SEO audit",
            "AI Manager chat",
        ],
    },
    {
        name: "Growth",
        price: "",
        period: "",
        highlight: true,
        blurb: "Businesses that want every channel handled in one place.",
        features: [
            "Up to 3 locations",
            "Everything in Starter",
            "Instagram comment moderation",
            "WhatsApp Business inbox",
            "Search Console & Analytics data",
            "AI search visibility & competitors",
        ],
    },
    {
        name: "Agency",
        price: "",
        period: "",
        blurb: "Agencies managing several client businesses.",
        features: [
            "Multiple client businesses",
            "Everything in Growth",
            "Per-client feature controls",
            "Weekly client reports",
            "Priority onboarding",
        ],
    },
];

export const FAQ = [
    {
        q: "What do I need to get started?",
        a: "Just your business name and city. A short description helps when your name is common. You can connect Instagram, WhatsApp and Google later from Settings.",
    },
    {
        q: "Will anything be posted without my say-so?",
        a: "Review replies, comment replies and fixes start in Approval mode and wait in your approvals inbox. On WhatsApp, simple questions about hours, location and services are answered automatically by default; prices, bookings and complaints wait for you, and you can change any of these. Whatever the setting, a message that mentions legal threats, injuries, medical or privacy issues — or a reply that uses a claim you've banned — is never sent without you.",
    },
    {
        q: "How does it find my profiles?",
        a: "It searches Google's business listings and the web for your name and city, then checks each candidate against your phone, address and website before suggesting it. You confirm every match.",
    },
    {
        q: "Which AI does it use, and is my data used for training?",
        a: "Drafts and analysis are produced by commercial AI models called through their APIs. Your business data is sent only to generate your results and is not used by us to train models.",
    },
    {
        q: "Can I use it for more than one business?",
        a: "Yes. Add as many companies as your plan allows and switch between them from the top bar. Each has its own profiles, settings and data.",
    },
    {
        q: "How do I remove my data?",
        a: "Disconnect any integration from Settings at any time, or email us to delete your account and all its data. See the Data deletion page for details.",
    },
];
